// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.
// Changed from hardcoded URLs to props passed from parent (which reads from site settings).

"use client";

import { useEffect, useRef } from "react";
import Script from "next/script";
import { oaiqMeasure } from "@/lib/oaiq";
import type { ConversionEvent } from "@/lib/blocks/schemas";

interface BookingEmbedProps {
  bookingUrl: string;
  bookingScriptUrl: string;
  // Controls whether the widget also reports a page view on mount.
  // Successful bookings always report appointment_scheduled.
  conversionEvent?: ConversionEvent;
}

// The LeadConnector calendar posts `['msgsndr-booking-complete', { calendarId }]`
// to the parent window when a visitor finishes booking. Community sources
// disagree on the sending origin — the widget's own host (e.g.
// link.opichi.com) or api.leadconnectorhq.com — so both are accepted until the
// staging test booking (#105) confirms which one.
const BOOKING_COMPLETE_MESSAGE = "msgsndr-booking-complete";
const LEADCONNECTOR_API_ORIGIN = "https://api.leadconnectorhq.com";

function originOf(url: string): string | null {
  try {
    return new URL(url).origin;
  } catch {
    return null;
  }
}

export function BookingEmbed({
  bookingUrl,
  bookingScriptUrl,
  conversionEvent = "widgetShown",
}: BookingEmbedProps) {
  // Measures queue if the pixel SDK hasn't loaded yet (consent-gated) and
  // flush on consent — see src/lib/oaiq.ts.
  //
  // widgetShown: record `page_viewed` when the booking widget mounts. (React
  // Strict Mode may double-invoke this in dev; fires once per load in
  // production.)
  useEffect(() => {
    if (conversionEvent !== "widgetShown") return;
    oaiqMeasure("page_viewed", { type: "contents" });
  }, [conversionEvent]);

  // Always report `appointment_scheduled` when the
  // calendar reports a finished booking. The message carries no booking ID,
  // so OpenAI cannot de-duplicate repeats — the ref caps it at one per
  // mounted widget, however many messages arrive (the ref survives Strict
  // Mode's effect re-run, so a dev double-mount cannot double-send either).
  const bookingReported = useRef(false);
  useEffect(() => {
    const allowedOrigins = new Set(
      [originOf(bookingUrl), LEADCONNECTOR_API_ORIGIN].filter(
        (o): o is string => o !== null
      )
    );
    function onMessage(e: MessageEvent) {
      if (bookingReported.current) return;
      if (!allowedOrigins.has(e.origin)) return;
      if (!Array.isArray(e.data) || e.data[0] !== BOOKING_COMPLETE_MESSAGE) {
        return;
      }
      bookingReported.current = true;
      oaiqMeasure("appointment_scheduled", { type: "customer_action" });
    }
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [bookingUrl]);

  if (!bookingUrl) return null;

  return (
    <>
      {/*
        LeadConnector booking widget. form_embed.js loads iframe-resizer, which
        initializes this iframe and reveals + height-syncs it after a postMessage
        handshake with the embedded calendar.

        WHY next/script (do NOT go back to manual appendChild): the previous
        implementation created the <script> in a useEffect and REMOVED it on
        unmount, re-adding it on every mount ("Reinitialize booking embed script
        on contact page mount"). Combined with React Strict Mode's double-invoke,
        that initialized iframe-resizer on the same iframe twice — the second
        init parked the iframe off-screen (position:absolute; left:-9999px;
        opacity:0; visibility:hidden) and never revealed it, so the widget only
        appeared after a hard refresh. next/script loads form_embed.js exactly
        once (deduped by src), keeps it across client-side navigations, and
        loads it after hydration so the iframe already exists — a single clean
        iframe-resizer init.

        min-height reserves space and guarantees the widget is visible even if
        the resize handshake is delayed; iframe-resizer overrides it once the
        calendar reports its real height.
      */}
      <iframe
        src={bookingUrl}
        style={{
          width: "100%",
          minHeight: "700px",
          border: "none",
          overflow: "hidden",
        }}
        scrolling="no"
        title="Book a Call"
      />
      {bookingScriptUrl && (
        <Script src={bookingScriptUrl} strategy="afterInteractive" />
      )}
    </>
  );
}
